import React, { useCallback, useEffect, useRef, useState } from 'react';
import Icon from 'components/styleguide/icon/Icon';
import Button from 'components/styleguide/button/Button';
import { useAutoSave } from 'components/styleguide/hooks/useAutoSave';

const styles = require('./ImageUpload.module.scss');

const MAX_FILE_SIZE = 5 * 1024 * 1024;
const MIN_DIMENSION = 280;
const ALLOWED_TYPES = ['image/jpeg', 'image/png'];

export interface ImageUploadProps {
  alt?: string;
  autoSaveUrl?: string;
  centered?: boolean;
  error?: string;
  imageUrl?: string;
  name?: string;
  onChange?: (file: File) => void;
  onError?: (error: string) => void;
  readOnly?: boolean;
}

const validateDimensions = (file: File): Promise<string | null> => {
  return new Promise((resolve) => {
    const img = new Image();
    img.onload = () => {
      URL.revokeObjectURL(img.src);
      if (img.width < MIN_DIMENSION || img.height < MIN_DIMENSION) {
        resolve(`Image must be at least ${MIN_DIMENSION}x${MIN_DIMENSION}px`);
      } else {
        resolve(null);
      }
    };
    img.onerror = () => {
      URL.revokeObjectURL(img.src);
      resolve('Could not read image file');
    };
    img.src = URL.createObjectURL(file);
  });
};

const ImageUpload: React.FC<ImageUploadProps> = ({
  alt = 'Uploaded image',
  autoSaveUrl,
  centered,
  error: externalError,
  imageUrl,
  name,
  onChange,
  onError,
  readOnly,
}) => {
  const { autoSaveError, save } = useAutoSave({
    url: autoSaveUrl || '',
    name: name || '',
    debounceMs: 0,
  });
  const fileInputRef = useRef<HTMLInputElement>(null);
  const [internalError, setInternalError] = useState<string | null>(null);
  const [dismissedExternalError, setDismissedExternalError] = useState(false);
  const visibleExternalError = dismissedExternalError ? null : externalError;

  useEffect(() => { if (autoSaveError) setInternalError(autoSaveError); }, [autoSaveError]);
  const [previewUrl, setPreviewUrl] = useState<string | null>(null);
  const [imageLoadFailed, setImageLoadFailed] = useState(false);

  useEffect(() => { setImageLoadFailed(false); }, [imageUrl, previewUrl]);

  useEffect(() => {
    return () => {
      if (previewUrl) {
        URL.revokeObjectURL(previewUrl);
      }
    };
  }, [previewUrl]);

  const handleClick = useCallback(() => {
    fileInputRef.current?.click();
  }, []);

  const handleFileChange = useCallback(async (e: React.ChangeEvent<HTMLInputElement>) => {
    const selected = e.target.files?.[0];
    if (!selected) return;

    if (!ALLOWED_TYPES.includes(selected.type)) {
      const msg = 'Only JPG or PNG files are allowed';
      setInternalError(msg);
      onError?.(msg);
      return;
    }

    if (selected.size > MAX_FILE_SIZE) {
      const msg = 'File size must be less than 5 MB';
      setInternalError(msg);
      onError?.(msg);
      return;
    }

    const dimensionError = await validateDimensions(selected);
    if (dimensionError) {
      setInternalError(dimensionError);
      onError?.(dimensionError);
      return;
    }

    setInternalError(null);
    setDismissedExternalError(true);
    setPreviewUrl((prev) => {
      if (prev) URL.revokeObjectURL(prev);
      return URL.createObjectURL(selected);
    });
    if (autoSaveUrl) save(selected);
    onChange?.(selected);
  }, [onChange, onError]);

  const displayUrl = previewUrl || imageUrl;
  const hasImage = !!displayUrl && !imageLoadFailed;
  const buttonLabel = hasImage ? 'Replace image' : 'Upload image';
  const buttonIcon = hasImage ? 'repeat' : 'upload';

  return (
    <div className={`${styles.wrapper}${centered ? ` ${styles.wrapperCentered}` : ''}`}>
      <div className={styles.container}>
        <div className={styles.row}>
          {hasImage ? (
            <img
              src={displayUrl}
              alt={alt}
              className={styles.image}
              onError={() => setImageLoadFailed(true)}
            />
          ) : (
            <div className={styles.placeholder} aria-hidden="true">
              <Icon name="camera" size="3xl" color="neutral-text-disable" />
            </div>
          )}
          {!readOnly && (
            <>
              <div className={styles.info}>
                <div className={styles.actions}>
                  <Button
                    type="terceary"
                    context="company"
                    size="S"
                    iconLeft={buttonIcon}
                    onClick={handleClick}
                    aria-label={buttonLabel}
                  >
                    {buttonLabel}
                  </Button>
                </div>
                {internalError ? (
                  <p className={styles.error} role="alert">{internalError}</p>
                ) : (
                  <p className={styles.constraints}>
                    Min. dimensions: 280x280px<br />
                    Max. file size: 5 MB<br />
                    JPG or PNG only
                  </p>
                )}
              </div>
              <input
                ref={fileInputRef}
                type="file"
                name={name}
                accept="image/jpeg,image/png"
                onChange={handleFileChange}
                className={styles.fileInput}
                aria-label="Choose image to upload"
                tabIndex={-1}
              />
            </>
          )}
        </div>
        {visibleExternalError && (
          <div className={styles.hintContainer} role="alert">
            <Icon name="triangleAlert" color="error-text-base" size="sm" aria-hidden="true" />
            <p className={styles.hintText}>{visibleExternalError}</p>
          </div>
        )}
      </div>
    </div>
  );
};

export default ImageUpload;
